import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { styled } from 'styled-components';
import * as uuid from 'uuid';

import { Loader } from '@vivaahealth/design-system';

import type { ChatConversation } from '../../common';
import { useConfiguration } from '../../hooks/useConfiguration';
import { ConfigurationContext } from '../../context/configurationContext';
import { ConversationContext } from '../../context/conversationContext';
import { ModalitiesConfigurationRoutes } from './routes';
import { loadedInIframe } from '../../utils/message';

import * as api from '../../api';

const AssistantRenders: React.FC = () => {
  const [messages, setMessages] = useState<ChatConversation[]>([]);
  const [pageHeight, setPageHeight] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [panelContent, setPanelContent] = useState<ReactNode | null>(null);
  const configuration = useConfiguration();

  const upsertLastMessage = useCallback(
    (
      newContent: ChatConversation,
      intentComponent?: api.Assistant.IntentComponent,
    ) => {
      setMessages((prevMessages) => {
        const indexToUpdate = intentComponent
          ? prevMessages.findLastIndex((msg) => {
              if (!('answer' in msg)) return false;
              if (msg.intentComponent !== intentComponent) return false;
              if (msg.disabled) return false;
              return true;
            })
          : prevMessages.length - 1;
        const messageToUpdate = prevMessages[indexToUpdate];
        if (!messageToUpdate || !('answer' in messageToUpdate)) {
          return [...prevMessages, newContent];
        }
        const updatedMessages = prevMessages.map((msg, i) => {
          if (i !== indexToUpdate) return msg;
          if (!('answer' in msg)) return msg;
          if (
            msg.chat_message_id !== '' &&
            msg.chat_message_id !== messageToUpdate.chat_message_id
          ) {
            return msg;
          }
          if (messageToUpdate.intentComponent) return messageToUpdate;
          if (msg.intentComponent) return msg;
          return { ...msg, ...newContent };
        });
        return updatedMessages;
      });
    },
    [],
  );

  const resetConversation = useCallback(() => {
    setMessages([]);
  }, []);

  const handleOnClose = useCallback(
    (sendEventToParent: boolean = true) => {
      setIsChatOpen(false);
      const { parent } = window;
      configuration.reset();
      resetConversation();
      if (!sendEventToParent) return;
      parent &&
        parent.postMessage({ action: 'assistantCloseConversation' }, '*');
    },
    [configuration, resetConversation],
  );

  const handleOnOpen = useCallback(() => {
    setIsChatOpen(true);
    const { parent } = window;
    if (!parent) return;
    parent.postMessage({ action: 'assistantFullPage' }, '*');
  }, []);

  const addNewMessage = useCallback((newMessage: ChatConversation) => {
    setMessages((prevMessages) => [
      ...prevMessages.map((msg) => ({ ...msg, disabled: true })),
      newMessage,
    ]);
  }, []);

  const handleResize = useCallback(() => {
    const contentHeight =
      window.document.getElementById('root')?.firstElementChild?.scrollHeight;
    if (!contentHeight) return;
    if (pageHeight === contentHeight) return;
    setPageHeight(contentHeight);
    const { parent } = window;
    if (!parent) return;
    parent.postMessage(
      {
        action: 'windowResize',
        params: { height: contentHeight },
      },
      '*',
    );
  }, [pageHeight, setPageHeight]);

  const receiveMessage = useCallback(
    (event: MessageEvent<any>) => {
      switch (event.data.action) {
        case 'addMessage':
          if (event.origin !== 'https://www.pcmahealth.org') return;
          if (!event.data.params.inputValue) return;
          addNewMessage({
            question: event.data.params.inputValue,
            chat_message_id: uuid.v4(),
            done: true,
            disabled: true,
          });
          return handleOnOpen();
        case 'postPromptToken':
          if (!event.data.params.tokenId) return;
          const prompt =
            configuration.takeoverButtonTokenToPromptMapping?.[
              event.data.params.tokenId
            ];
          if (!prompt) return;
          addNewMessage({
            question: prompt,
            chat_message_id: uuid.v4(),
            done: true,
            disabled: true,
          });
          return handleOnOpen();
        case 'closeConversation':
          return handleOnClose(false);
        default:
          return;
      }
    },
    [
      addNewMessage,
      handleOnOpen,
      configuration.takeoverButtonTokenToPromptMapping,
      handleOnClose,
    ],
  );

  useEffect(() => {
    handleResize();
  }, [handleResize, configuration?.promptQuestions, configuration.isLoading]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('message', receiveMessage);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('message', receiveMessage);
    };
  }, [handleResize, receiveMessage]);

  const updateCurrentMessage = useCallback((newContent: ChatConversation) => {
    setMessages((prevMessages) => {
      if (!('chat_message_id' in newContent)) return prevMessages;
      return prevMessages.map((message) => {
        if (!('chat_message_id' in message)) return message;
        if (message.chat_message_id !== newContent.chat_message_id) {
          return message;
        }
        return newContent;
      });
    });
  }, []);

  const removeLastMessage = useCallback(() => {
    setMessages((prevMessages) =>
      prevMessages
        .filter((_, i) => (i === prevMessages.length - 1 ? false : true))
        .map((msg, i) =>
          i === prevMessages.length - 2 ? { ...msg, disabled: false } : msg,
        ),
    );
  }, []);

  const disableMessages = useCallback((intent?: api.Assistant.Intent) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (!('intent' in msg)) return msg;
        if (intent && msg.intent !== intent) return msg;
        return { ...msg, disabled: true };
      }),
    );
  }, []);

  if (configuration.isLoading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  const chatProps = { handleOnClose, handleOnOpen, handleResize, isChatOpen };
  return (
    <ConfigurationContext.Provider
      value={{
        ...configuration,
        isEmbedded: loadedInIframe(),
      }}
    >
      <ConversationContext.Provider
        value={{
          messages,
          upsertLastMessage,
          updateCurrentMessage,
          addNewMessage,
          disableMessages,
          resetConversation,
          removeLastMessage,
          setPanelContent,
          panelContent,
        }}
      >
        <Routes>
          {Object.values(ModalitiesConfigurationRoutes)
            .filter((value) => !!value.path)
            .map((value) => (
              <Route
                key={value.path}
                element={value.component(chatProps)}
                path={value.path}
              />
            ))}
        </Routes>
      </ConversationContext.Provider>
    </ConfigurationContext.Provider>
  );
};

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

export default AssistantRenders;
